// === library ===
import React, {
  useEffect,
  useState,
  useReducer,
  useCallback,
  useRef,
} from "react";
import axios from "axios";
import {
  Platform,
  Image,
  TouchableWithoutFeedback,
  View,
  Dimensions,
  BackHandler,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { HStack, KeyboardAvoidingView, useToast } from "native-base";
import { translate } from "../../locales/translate";
import * as faCharCodes from "../../../assets/consts/faCharCodes";
import * as AppActions from "../../actions/AppActions";
// import * as UserActions from "../../actions/UserActions";
import * as CONSTS from "../../../assets/consts";
import * as SecureStore from "../../components_v2/DeviceAsyncStorage";
// === Components ===
import FontAwesomeIcon from "../../components_v2/FontAwesomeIcon";
import InputBase from "../../nativeBase-ui/InputBase";
import { ArButton } from "../../argon-ui";
import Theme from "../../argon-ui/Theme";
import TextFont from "../../components_v2/TextFont";
import LoginLayout from "./LoginLayout";
import ServerList from "../../components_v2/Servers/ServerList";
// === Modules ===
import DeviceInfoHandler from "../../modules/deviceInfo/DeviceInfoHandler";
import ActionSheet from "../../nativeBase-ui/ActionSheet";
import UserProfile from "../../components/UserProfile";
import ToastAlert from "../../nativeBase-ui/ToastAlert";
import ModalBase from "../../nativeBase-ui/ModalBase";
import ServerInfo from "../../viewModels/ServerInfo";
import BuildingInfo from "../../viewModels/BuildingInfo";
// === Hooks ===
import buildingsInfoGenerate from "../../hooks/buildingsInfoGenerate";
import { onFirebaseHandler } from "../../modules/firebaseHandler/onFirebaseHandler";
import { persianToEnglishNumber } from "../../util";
import checkIPAddress from "../../hooks/checkIPAddress";
import generateServersInfoByBuildings from "../../hooks/generateServersInfoByBuildings";
import { tryConnect } from "../../hooks/connectToServer/connectToBuilding";
import CaptchaCode from "../../components_v2/CaptchaCode";
import Text from "../../components_v2/TextFont";

const IS_WEB = Platform.OS === "web";
const IS_MOBILE = Platform.OS !== "web";
const LOGO_SIZE = 200;
const INPUT_HEIGHT = 50;
// === Interface ===
interface Props {
  navigation: any;
}
type FormState = {
  phoneNumber: string;
  password: string;
  captchaCode: string | null;
};
const initialState: FormState = {
  phoneNumber: "",
  password: "",
  captchaCode: null,
};
type FormValidityState = {
  phoneNumberError: boolean;
  phoneNumberErrorMessage: string;
  passwordError: boolean;
  passwordErrorMessage: string;
  captchaCodeError: boolean;
  captchaCodeErrorMessage: string;
  isFormValid: boolean;
};
const initialValidityState: FormValidityState = {
  phoneNumberError: false,
  phoneNumberErrorMessage: "",
  passwordError: false,
  passwordErrorMessage: "",
  captchaCodeError: false,
  captchaCodeErrorMessage: "",
  isFormValid: false,
};
type FormAction = {
  type: string;
  payLoad: string;
};
type FormValidityAction = {
  type: string;
  payLoad: FormState;
};

const formReducer = (state: FormState, action: FormAction): FormState => {
  switch (action.type) {
    case "UPDATE_PHONE_NUMBER":
      return {
        ...state,
        phoneNumber: action.payLoad,
      };
    case "UPDATE_PASSWORD":
      return {
        ...state,
        password: action.payLoad,
      };
    case "UPDATE_CAPTCHA_CODE":
      return {
        ...state,
        captchaCode: action.payLoad,
      };
    default:
      return state;
  }
};
const formValidityReducer = (
  state: FormValidityState,
  action: FormValidityAction
): FormValidityState => {
  let isValid: boolean = false;
  let IsRequired: string = "";
  switch (action.type) {
    case "VALIDATE_PHONE_NUMBER":
      isValid = action.payLoad.phoneNumber.length > 9 ? true : false;
      IsRequired =
        action.payLoad.phoneNumber.length == 0
          ? translate("login.errors.EmptyUserName")
          : !isValid
          ? translate("login.errors.InvalidPhoneNumber")
          : "";
      return {
        ...state,
        ...{
          phoneNumberError: !!IsRequired,
          isFormValid:
            IsRequired == "" && !state.passwordError && !state.captchaCodeError,
          phoneNumberErrorMessage: IsRequired,
        },
      };
    case "VALIDATE_PASSWORD":
      isValid = action.payLoad.password.length > 3 ? true : false;
      IsRequired =
        action.payLoad.password.length == 0
          ? translate("login.errors.requiredPassword")
          : !isValid
          ? translate("login.errors.ShortPassword")
          : "";
      return {
        ...state,
        ...{
          passwordError: !!IsRequired,
          isFormValid:
            isValid &&
            IsRequired == "" &&
            !state.phoneNumberError &&
            !state.captchaCodeError,
          passwordErrorMessage: IsRequired,
        },
      };
    case "VALIDATE_CAPTCHA_CODE":
      IsRequired = !action.payLoad.captchaCode
        ? translate("login.errors.captchaCodeIsRequired")
        : "";
      return {
        ...state,
        ...{
          captchaCodeError: !!IsRequired,
          isFormValid:
            IsRequired == "" && !state.phoneNumberError && !state.passwordError,
          captchaCodeErrorMessage: IsRequired,
        },
      };
    default:
      return state;
  }
};
const LoginScreen = (props: Props) => {
  const { navigation } = props;
  const toast = useToast();
  const captchaCodeRef = useRef<any>(null);

  // === React-redux ===
  const dispatch = useDispatch();
  const AppReducer = useSelector((state: any) => state.AppReducer);
  // === Form Validation ===
  const [formData, setFormData] = useReducer(formReducer, initialState);
  const [formValidityData, setFormValidityData] = useReducer(
    formValidityReducer,
    initialValidityState
  );

  const [captchaId, setCaptchaId] = useState<string | null>(null);
  const [serverUrl, setServerUrl] = useState<string>(AppReducer?.serverUrl);
  SecureStore.getServerUrl().then((serverUrl: string | null) => {
    setServerUrl(serverUrl || "");
  });
  const [serverModal, setServerModal] = useState<boolean>(false);
  const [spinnerLoading, setSpinnerLoading] = useState<boolean>(false);
  const [onOpenActionSheet, setOnOpenActionSheet] = useState<boolean>(false);
  const [validCaptchaCode, setValidCaptchaCode] = useState<boolean>(false);
  const [buildingsList, setBuildingsList] = useState<BuildingInfo[] | []>([]);
  const [accessTokenState, setAccessTokenState] = useState("");
  const [exitApp, setExitApp] = useState(0);
  // === show Password Icon ===
  const [showPassword, setShowPassword] = useState(false);
  const [isKioskMode, setIsKioskMode] = useState(true);
  const handleValidationFunction = () => {
    setFormValidityData({
      type: "VALIDATE_PHONE_NUMBER",
      payLoad: formData,
    });
    setFormValidityData({
      type: "VALIDATE_PASSWORD",
      payLoad: formData,
    });
    validCaptchaCode &&
      setFormValidityData({
        type: "VALIDATE_CAPTCHA_CODE",
        payLoad: formData,
      });
    if (
      formData.password.length > 3 &&
      formData.phoneNumber.length > 9 &&
      (validCaptchaCode
        ? formData.captchaCode && formData.captchaCode?.length !== 0
        : true)
    ) {
      formValidityData.isFormValid && fetchDataLogin();
    }
  };

  const listener = (event: any): void => {
    if (event.code === "Enter" || event.code === "NumpadEnter") {
      handleValidationFunction();
    }
  };
  const ToastDetails = [
    {
      title: translate("login.errors.DefaultError"),
      variant: "subtle",
      status: "error",
    },
    {
      title: translate("login.errors.AccountDisabled"),
      variant: "subtle",
      status: "error",
    },
    {
      title: translate("login.errors.undefinedError"),
      variant: "subtle",
      status: "error",
    },
  ];
  // === find current server url ===
  // SecureStore.getServerUrl().then((serverUrl: string | null) => {
  //   setServerUrl(serverUrl || "");
  // });
  // useEffect(() => {}, [AppReducer.currentServer.publicAddress]);

  // === Kiosk Mode handler  ===
  const KioskMode = () => {
    var devInfo = new DeviceInfoHandler();
    let isKioskModeDevice: boolean = devInfo.isKioskMode();
    setIsKioskMode(isKioskModeDevice);
    dispatch(AppActions.updateKioskMode(isKioskModeDevice));
  };
  // === first Load ===
  useEffect(() => {
    KioskMode();
    setServerUrl(AppReducer?.serverUrl);
  }, []);
  // === update Kiosk with change width size ===
  useEffect(() => {
    Dimensions.addEventListener("change", () => KioskMode());

    return () => {
      Dimensions.removeEventListener("change", () => KioskMode());
    };
  }, []);
  // === submit with Enter key ===
  useEffect(() => {
    if (Platform.OS == "web") {
      document.addEventListener("keydown", listener);
      return () => {
        document.removeEventListener("keydown", listener);
      };
    }
  }, [formData]);

  // Double back press to Exit App
  const backAction = useCallback(() => {
    if (navigation.isFocused()) {
      setTimeout(() => {
        setExitApp(0);
      }, 3000); // 3 seconds to tap second-time

      if (exitApp === 0) {
        setExitApp(exitApp + 1);
        const toastDetail = {
          title: translate("app.backButtonPress"),
          variant: "subtle",
          status: "info",
        };
        toast.show({
          duration: 3000,
          render: () => {
            return <ToastAlert {...toastDetail} />;
          },
        });
      } else if (exitApp === 1) {
        BackHandler.exitApp();
      }
      return true;
    }
    return false;
  }, [exitApp]);
  useEffect(() => {
    const backHandler = BackHandler.addEventListener(
      "hardwareBackPress",
      backAction
    );
    return () => backHandler.remove();
  }, [exitApp]);
  const fetchDataLogin = () => {
    var _serverUrl = serverUrl;
    setSpinnerLoading(true);
    if (!_serverUrl?.endsWith("/")) {
      _serverUrl += "/";
    }
    const UserName = persianToEnglishNumber(formData.phoneNumber);
    if (_serverUrl) {
      axios
        .post(
          `${_serverUrl}Auth/Login`,
          {
            UserName,
            Password: formData.password,
            captchaCode: formData.captchaCode,
            captchaId,
          },
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          }
        )
        .then((res) => {
          const {
            access_token,
            userFullName,
            userId,
            buildings,
            authenticated,
            errors,
            serverId,
            cloudServerId,
            ports,
          } = res.data;
          if (res.status == 200) {
            if (authenticated) {
              handleOnLogin(
                serverId,
                cloudServerId,
                access_token,
                userFullName,
                userId,
                buildings,
                setSpinnerLoading
              );
            } else {
              const messageModel = {
                title: translate(`login.errors.${errors[0]}`),
                variant: "subtle",
                status: "error",
              };
              toast.show({
                render: () => {
                  return <ToastAlert {...messageModel} />;
                },
              });
              setSpinnerLoading(false);
              captchaCodeRef && captchaCodeRef?.current?.refreshCaptcha();
            }
          }
        })
        .catch((error) => {
          console.log("login error", error);
          toast.show({
            render: () => {
              return <ToastAlert {...ToastDetails[2]} />;
            },
          });
          setSpinnerLoading(false);
          captchaCodeRef && captchaCodeRef?.current?.refreshCaptcha();
        });
    }
  };

  const forceInitHandler = async (accessToken: string, building = null) => {
    const currentServer = await SecureStore.Servers.getCurrentServer();
    // dispatch(AppActions.updateCurrentServer(currentServer));
    dispatch(AppActions.ForceInit(accessToken, building));
  };
  const handleOnLogin = async (
    serverId: string,
    cloudServerId: string,
    token: string,
    userFullName: string,
    userId: string,
    buildings: any = [],
    setSpinnerLoading: any
  ) => {
    // === clear cache for time change sever inside app ===
    dispatch(AppActions.clearCache());
    dispatch(AppActions.updateServerUrl(serverUrl));
    var _serverUrl = serverUrl;
    if (!_serverUrl?.endsWith("/")) {
      _serverUrl += "/";
    }
    var _serverUrlPublicCheck: string | null = null;
    var _serverUrlLocalCheck: string | null = null;

    if (_serverUrl && checkIPAddress(_serverUrl)) {
      _serverUrlLocalCheck = _serverUrl;
    } else {
      _serverUrlPublicCheck = _serverUrl;
    }
    // === get Ports from currentServer (AppReducer) ===
    const ports = AppReducer.currentServer?.ports;
    const serverInfo: ServerInfo = {
      serverId,
      publicAddress: _serverUrlPublicCheck,
      localAddress: _serverUrlLocalCheck,
      accessToken: token,
      userFullName,
      userId,
      ports,
    };
    dispatch(AppActions.updateCurrentServer(serverInfo));
    SecureStore.setItemAsync(CONSTS.USER_ID, userId);
    SecureStore.setItemAsync(CONSTS.ACCESS_TOKEN, token);
    setAccessTokenState(token);
    if (!cloudServerId) {
      const cloudServerInfo = new ServerInfo(
        serverId,
        serverUrl,
        null,
        token,
        userFullName,
        userId,
        ports
      );
      if (buildings.length !== 0) {
        const buildingsInfo = buildingsInfoGenerate(buildings);
        dispatch(AppActions.setBuildings(buildingsInfo));
        // === create serversInfo by buildings ===
        let newServersInfoByBuilding = await generateServersInfoByBuildings(
          buildings,
          token,
          serverUrl,
          ports
        );
        // === add proxy server ===
        newServersInfoByBuilding.forEach((serverInfo: ServerInfo) => {
          if (serverInfo.serverId !== cloudServerInfo.serverId) {
            newServersInfoByBuilding.push(cloudServerInfo);
          }
        });
        dispatch(AppActions.setServers(newServersInfoByBuilding));

        if (buildingsInfo && buildingsInfo.length > 1) {
          setBuildingsList(buildingsInfo);
          setOnOpenActionSheet(true);
        } else if (buildingsInfo.length == 1) {
          onProfileSelect(buildingsInfo[0], token);
        } else {
          forceInitHandler(token);
          navigation.replace("Home");
        }
      } else {
        dispatch(AppActions.addServer(serverInfo));
        setSpinnerLoading(false);
        forceInitHandler(token);
        navigation.replace("Home");
      }
    } else {
      const checkConnectionToLocal =
        _serverUrl && (await tryConnect(_serverUrl, 1500));

      if (buildings.length !== 0) {
        const buildingsInfo = await buildingsInfoGenerate(buildings);
        dispatch(AppActions.setBuildings(buildingsInfo));
        // === add buildings to sever list ===
        const newServersInfoByBuilding = await generateServersInfoByBuildings(
          buildings,
          token,
          checkConnectionToLocal ? checkConnectionToLocal.publicAddress : null,
          ports
        );
        // if (checkConnectionToLocal) {
        //   const cloudServerInfo = new ServerInfo(
        //     cloudServerId,
        //     checkConnectionToLocal.publicAddress,
        //     null,
        //     token,
        //     userFullName,
        //     userId,
        //     ports
        //   );
        //   // === add proxy server ===
        //   newServersInfoByBuilding.forEach((serverInfo: ServerInfo) => {
        //     if (serverInfo.serverId !== cloudServerInfo.serverId) {
        //       newServersInfoByBuilding.push(cloudServerInfo);
        //     }
        //   });
        // }
        dispatch(AppActions.setServers(newServersInfoByBuilding));

        if (buildingsInfo && buildingsInfo.length > 1) {
          setBuildingsList(buildingsInfo);
          setOnOpenActionSheet(true);
        } else if (buildingsInfo.length == 1) {
          onProfileSelect(buildingsInfo[0], token);
        } else {
          forceInitHandler(token);
          navigation.replace("Home");
        }
      } else {
        dispatch(AppActions.addServer(serverInfo));
        if (checkConnectionToLocal) {
          const cloudServerInfo = new ServerInfo(
            cloudServerId,
            checkConnectionToLocal.cloudPublicAddress,
            null,
            token,
            userFullName,
            userId,
            ports
          );
          setTimeout(() => {
            dispatch(AppActions.addServer(cloudServerInfo));
          }, 100);
        }
        setSpinnerLoading(false);
        forceInitHandler(token);
        navigation.replace("Home");
      }
    }
    setSpinnerLoading(false);
    setTimeout(() => {
      setFormData({ type: "UPDATE_PASSWORD", payLoad: "" });
      setFormData({ type: "UPDATE_PHONE_NUMBER", payLoad: "" });
      setFormData({ type: "UPDATE_CAPTCHA_CODE", payLoad: "" });
      setCaptchaId(null);
    }, 1000);
  };

  const onProfileSelect = async (
    profile: BuildingInfo,
    accessToken: string
  ) => {
    const { navigation } = props;
    SecureStore.setAccessToken(profile.buildingId, accessToken);
    SecureStore.setItemAsync(CONSTS.ACCESS_TOKEN, accessToken);
    dispatch(AppActions.updateCurrentBuilding(profile));
    onFirebaseHandler(dispatch, accessToken);
    dispatch(AppActions.ForceInit(accessToken, profile.buildingId));

    navigation.replace("Home");
  };

  const handleOnServerChange = async (
    serverInfo: ServerInfo,
    addressConnected: string
  ) => {
    let { publicAddress, serverId, accessToken, localAddress } = serverInfo;
    var _serverUrl = serverUrl;

    if (addressConnected?.endsWith("/")) {
      addressConnected = addressConnected.slice(0, -1);
    }
    if (serverUrl?.endsWith("/")) {
      _serverUrl = serverUrl.slice(0, -1);
    }

    setServerUrl(addressConnected);
    captchaCodeRef && captchaCodeRef?.current?.refreshCaptcha();
    if (addressConnected && addressConnected !== _serverUrl) {
      dispatch(AppActions.updateServerUrl(addressConnected));
      dispatch(AppActions.updateCurrentServer(serverInfo));
      dispatch(AppActions.ResetConnection(addressConnected));
    }

    if (accessToken && accessToken !== "") {
      var buildingsList = AppReducer.buildings;
      buildingsList = buildingsList.filter(
        (building: BuildingInfo) =>
          building.serverId == serverId || building.cloudServerId === serverId
      );
      if (buildingsList && buildingsList.length > 1) {
        setBuildingsList(buildingsList);
        setOnOpenActionSheet(true);
      } else if (buildingsList.length == 1) {
        onProfileSelect(buildingsList[0], accessToken);
      } else {
        forceInitHandler(accessToken);
        navigation.replace("Home");
      }
    } else {
      SecureStore.Servers.addServer(serverInfo);
      SecureStore.setItemAsync(
        CONSTS.SERVER_URL,
        addressConnected || publicAddress || localAddress || ""
      );

      dispatch(AppActions.updateCurrentServer(serverInfo));
    }
  };
  return (
    <LoginLayout>
      <KeyboardAvoidingView
        justifyContent="center"
        h="100%"
        style={{ flex: 1, height: "100%", position: "relative" }}
        w={isKioskMode ? "50%" : "100%"}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <View style={{ justifyContent: "center", alignItems: "center" }}>
          {!isKioskMode && (
            <Image
              source={CONSTS?.BRANDING?.LOGO_TYPE}
              // width={LOGO_SIZE}
              // height={LOGO_SIZE}
              // style={styles.logo}
              style={{
                width: LOGO_SIZE,
                height: 50,
                resizeMode: "contain",
              }}
            />
          )}
          <TextFont
            style={{
              margin: 5,
              paddingVertical: 3,
              textAlign: "center",
              color: Theme.COLORS.MUTED,
              fontFamily: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif`,
            }}
            {...(IS_MOBILE && { onPress: () => setServerModal(true) })}
            // onPress={() => setServerModal(true)}
          >
            <>
              <FontAwesomeIcon
                icon={IS_WEB ? faCharCodes.faGlobe : faCharCodes.faEdit}
                color="MUTED"
                size={14}
              />{" "}
              {serverUrl?.endsWith("/") ? serverUrl.slice(0, -1) : serverUrl}
            </>
          </TextFont>
        </View>
        <View style={{ justifyContent: "center", alignItems: "center" }}>
          <View
            style={{
              paddingVertical: 10,
              width: "90%",
              maxWidth: isKioskMode ? "100%" : "90%",
            }}
          >
            <TextFont
              style={{
                fontSize: 18,
                fontWeight: "600",
                color: Theme.COLORS.MUTED,
              }}
            >
              {translate("login.loginHeading")}
            </TextFont>
            {/* === Form === */}
            <View style={{ marginTop: 5 }}>
              <InputBase
                inputStyle={{ height: INPUT_HEIGHT, fontSize: 18 }}
                showSubInput={true}
                keyboardType="numeric"
                isInvalid={formValidityData.phoneNumberError}
                errorMessage={formValidityData.phoneNumberErrorMessage}
                value={formData.phoneNumber}
                onChangeText={(phoneNumber: string) =>
                  setFormData({
                    type: "UPDATE_PHONE_NUMBER",
                    payLoad: phoneNumber,
                  })
                }
                placeholder={translate("login.phoneNumber")}
                onBlur={() =>
                  setFormValidityData({
                    type: "VALIDATE_PHONE_NUMBER",
                    payLoad: formData,
                  })
                }
                onKeyPress={(e: any) => {
                  if (formData.phoneNumber.length > 9) {
                    setFormValidityData({
                      type: "VALIDATE_PHONE_NUMBER",
                      payLoad: formData,
                    });
                    listener(e);
                  }
                }}
                onSubmitEditing={() => {
                  if (formData.phoneNumber.length > 9 && !IS_WEB) {
                    setFormValidityData({
                      type: "VALIDATE_PHONE_NUMBER",
                      payLoad: formData,
                    });
                    handleValidationFunction();
                  }
                }}
                icon={faCharCodes.faPhoneAlt}
              />
              {/* === Password Input === */}
              <InputBase
                inputStyle={{ height: INPUT_HEIGHT, fontSize: 18 }}
                showSubInput={true}
                isInvalid={formValidityData.passwordError}
                errorMessage={formValidityData.passwordErrorMessage}
                value={formData.password}
                onChangeText={(password: string) =>
                  setFormData({ type: "UPDATE_PASSWORD", payLoad: password })
                }
                placeholder={translate("login.password")}
                onBlur={() => {
                  formData.password.length > 3 &&
                    setFormValidityData({
                      type: "VALIDATE_PASSWORD",
                      payLoad: formData,
                    });
                }}
                onKeyPress={(e: any) => {
                  if (formData.password.length > 3) {
                    setFormValidityData({
                      type: "VALIDATE_PASSWORD",
                      payLoad: formData,
                    });
                    listener(e);
                  }
                }}
                onSubmitEditing={() => {
                  if (formData.password.length > 3 && !IS_WEB) {
                    setFormValidityData({
                      type: "VALIDATE_PASSWORD",
                      payLoad: formData,
                    });
                    handleValidationFunction();
                  }
                }}
                icon={faCharCodes.faLockAlt}
                secureTextEntry={!showPassword}
                iconLeft={
                  <TouchableWithoutFeedback
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <View
                      style={{
                        justifyContent: "center",
                        alignItems: "center",
                        paddingHorizontal: 14,
                      }}
                    >
                      <FontAwesomeIcon
                        icon={
                          showPassword
                            ? faCharCodes.faEyeSlash
                            : faCharCodes.faEye
                        }
                        color={Theme.COLORS.MUTED}
                      />
                    </View>
                  </TouchableWithoutFeedback>
                }
              />
              {/* === Captcha Code === */}
              <CaptchaCode
                ref={captchaCodeRef}
                setValidCaptchaCode={setValidCaptchaCode}
                setCaptchaId={(captchaId) => {
                  setCaptchaId(captchaId);
                }}
                captchaId={captchaId}
                captchaCode={formData.captchaCode}
                setCaptchaCode={(code) => {
                  setFormData({ type: "UPDATE_CAPTCHA_CODE", payLoad: code });
                }}
                onBlur={() => {
                  setFormValidityData({
                    type: "VALIDATE_CAPTCHA_CODE",
                    payLoad: formData,
                  });
                }}
                onKeyPress={(e: any) => {
                  if (formData.captchaCode?.length !== 0) {
                    setFormValidityData({
                      type: "VALIDATE_CAPTCHA_CODE",
                      payLoad: formData,
                    });
                    listener(e);
                  }
                }}
                onSubmitEditing={() => {
                  if (formData.phoneNumber?.length !== 0 && !IS_WEB) {
                    setFormValidityData({
                      type: "VALIDATE_CAPTCHA_CODE",
                      payLoad: formData,
                    });
                    handleValidationFunction();
                  }
                }}
                errorMessage={formValidityData.captchaCodeErrorMessage}
              />
              {/* === Submit Btn === */}
              <View style={{ marginTop: 10 }}>
                <ArButton
                  isLoading={spinnerLoading}
                  onPress={() => {
                    handleValidationFunction();
                  }}
                  style={{ marginButton: 15 }}
                >
                  {translate("login.login")}
                </ArButton>
                <ArButton
                  variant="ghost"
                  onPress={() => navigation.replace("SignInScreen")}
                >
                  {translate("login.createAccount")}
                </ArButton>
                <TouchableWithoutFeedback
                  style={{ marginTop: 5 }}
                  onPress={() => navigation.replace("ResetPasswordScreen")}
                >
                  <HStack
                    space="1"
                    alignItems="center"
                    justifyContent="center"
                    py="1"
                  >
                    <TextFont
                      style={{ color: Theme.COLORS.GRAY_DARK, fontSize: 10 }}
                    >
                      {translate("login.goToResetPasswordDescription")}
                    </TextFont>
                    <TextFont
                      style={{
                        color: Theme.COLORS.PRIMARY,
                        fontSize: 10,
                        borderBottomWidth: 1,
                        borderColor: Theme.COLORS.PRIMARY,
                      }}
                    >
                      {translate("login.goToResetPassword")}
                    </TextFont>
                  </HStack>
                </TouchableWithoutFeedback>
              </View>
            </View>
          </View>
        </View>
        <View
          style={{
            position: "absolute",
            bottom: 5,
            left: 0,
            right: 0,
            alignItems: "center",
          }}
        >
          <Text style={{ fontSize: 10 }}>{CONSTS.APP_VERSION}</Text>
        </View>
      </KeyboardAvoidingView>
      {/* === Server Modal === */}
      {serverModal && (
        <ModalBase
          header={translate("user_profile.serversList")}
          isOpen={serverModal}
          onClose={() => setServerModal(false)}
          contentStyle={{ height: "100%" }}
        >
          <ServerList
            onCancel={() => {
              setServerModal(false);
            }}
            onSelect={(serverInfo: ServerInfo, addressConnected: string) => {
              setServerModal(false);
              handleOnServerChange(serverInfo, addressConnected);
            }}
          />
        </ModalBase>
      )}
      {/* === BuildingList Sheet */}
      {onOpenActionSheet && (
        <ActionSheet
          isOpen={onOpenActionSheet}
          header={translate("user_profile.pickAProfile")}
        >
          {buildingsList.length > 0 &&
            buildingsList.map((b: any, i: any) => {
              return (
                <UserProfile
                  key={i}
                  style={{ marginVertical: 5 }}
                  buildingId={b.buildingId}
                  buildingName={b.buildingName}
                  userId={b.userId}
                  userFullName={b.userDisplayName}
                  serverId={b.serverId}
                  selected={false}
                  onSelect={() => {
                    setOnOpenActionSheet(false);
                    onProfileSelect(b, accessTokenState);
                  }}
                />
              );
            })}
        </ActionSheet>
      )}
    </LoginLayout>
  );
};

export default LoginScreen;
